// Main intake form page. Builds across milestones 4–9.
//
// M4: area type picker + county/city selectors + stats cards.
// M6: SVG location highlight.
// M7: 3-column layout + interactive product checkboxes + estimate summary.
// Layout is co-located here as `sx`; all decorative styling lives in the theme.
import {
  useEffect, useCallback, useState, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router';
import {
  Box, Typography, IconButton, useTheme, useMediaQuery,
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { fetchCatalog } from '../store/catalog-slice.js';
import {
  clearSelections, scheduleEstimate, resetEstimate,
} from '../store/selections-slice.js';
import { setAreaType, setAreaRefId } from '../store/area-slice.js';
import { resetInquiry } from '../store/inquiry-slice.js';
import { fetchIntakeSession, discardDraft } from '../store/contact-slice.js';
import { hydrateDraft } from '../store/draft-sync.js';
import { selectSettings, selectPartnerLogos } from '../store/settings-slice.js';
import { fetchParticipation, selectParticipationMap } from '../store/participation-slice.js';
import { layout } from '../theme.js';
import AreaTypePicker from '../components/AreaTypePicker.jsx';
import CountyDropdown from '../components/CountyDropdown.jsx';
import CityDropdown from '../components/CityDropdown.jsx';
import CustomGeometryUploader from '../components/CustomGeometryUploader.jsx';
import { StatsPanel } from '../components/StatsCards.jsx';
import LocationHighlightSvg from '../components/location-highlight/LocationHighlightSvg.jsx';
import ProductColumn from '../components/products/ProductColumn.jsx';
import IncludedProductsAnnouncer from '../components/products/IncludedProductsAnnouncer.jsx';
import EstimateSummaryPanel from '../components/estimate/EstimateSummaryPanel.jsx';
import EstimateSummaryDrawer from '../components/estimate/EstimateSummaryDrawer.jsx';
import SendInquiryModal from '../components/intake/SendInquiryModal.jsx';
import ResumeDraftBanner from '../components/intake/ResumeDraftBanner.jsx';
import ThemeModeToggle from '../components/ThemeModeToggle.jsx';
import SanbornLogo from '../components/SanbornLogo.jsx';
import LogoBar from '../components/LogoBar.jsx';
import useOverflow from '../hooks/useOverflow.js';
import scrollByPage from '../utils/scroll-by-page.js';

// Width of the edge fade that hints at horizontally off-screen categories.
const CATEGORY_FADE_PX = 48;

// Mask that fades the grid's content toward the container edges so off-screen
// categories read as "continuing". Uses alpha only, so it works in any theme
// (it reveals whatever sits behind the grid). `undefined` when nothing overflows.
const categoryFadeMask = (left, right) => {
  if (!left && !right) return undefined;
  if (left && right) {
    return `linear-gradient(to right, transparent 0, #000 ${CATEGORY_FADE_PX}px, #000 calc(100% - ${CATEGORY_FADE_PX}px), transparent 100%)`;
  }
  if (left) return `linear-gradient(to right, transparent 0, #000 ${CATEGORY_FADE_PX}px)`;
  return `linear-gradient(to right, #000 calc(100% - ${CATEGORY_FADE_PX}px), transparent 100%)`;
};

const IntakeFormPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [inquiryOpen, setInquiryOpen] = useState(false);

  const catalog = useSelector((state) => state.catalog);
  const areaType = useSelector((state) => state.area.areaType);
  const stats = useSelector((state) => state.area.stats);
  const prevAreaType = useSelector((state) => state.area.areaType);
  const {
    verified, sessionChecked, draft,
  } = useSelector((state) => state.contact);
  const settings = useSelector(selectSettings);
  const logos = useSelector(selectPartnerLogos);
  const participation = useSelector(selectParticipationMap);

  // Horizontal overflow of the category grid: drives the edge fades + the
  // left/right "more categories" chips so off-screen columns are discoverable.
  const gridRef = useRef(null);
  const categoryCount = catalog.data?.categories.length ?? 0;
  const { canLeft, canRight } = useOverflow(gridRef, [categoryCount, isMobile]);

  const scrollCategories = useCallback((dir) => {
    const el = gridRef.current;
    if (!el) return;
    scrollByPage(el, { left: el.clientWidth * 0.8 * dir });
  }, []);

  useEffect(() => {
    if (!catalog.data && !catalog.loading) dispatch(fetchCatalog());
  }, [catalog.data, catalog.loading, dispatch]);

  // Load the county participation map for the sidebar choropleth. Non-fatal —
  // on failure every county simply renders as the default 'interest'.
  useEffect(() => {
    dispatch(fetchParticipation());
  }, [dispatch]);

  // A direct visit / refresh won't have the verified flag in memory yet; check
  // the visitor cookie before deciding whether to admit or bounce.
  useEffect(() => {
    if (!sessionChecked) dispatch(fetchIntakeSession());
  }, [sessionChecked, dispatch]);

  // Rehydrate a saved draft exactly once after verification resolves.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (verified && draft && draft.hasContent && !hydratedRef.current) {
      hydratedRef.current = true;
      dispatch(hydrateDraft(draft));
    }
  }, [verified, draft, dispatch]);

  // Clear selections when the area type changes so stale estimates don't linger.
  useEffect(() => {
    dispatch(clearSelections());
  }, [prevAreaType, dispatch]);

  // Re-price existing selections when the geometry itself changes (e.g. the
  // user picks a different county or re-uploads a custom file). The token is
  // what the pricing call signs against, so a new token means the previous
  // estimate no longer applies.
  const geometryToken = stats.data?.geometryToken ?? null;
  const hasSelections = useSelector((state) => state.selections.items.length > 0);
  useEffect(() => {
    if (geometryToken && hasSelections) {
      dispatch(resetEstimate());
      dispatch(scheduleEstimate(geometryToken));
    }
  }, [geometryToken, hasSelections, dispatch]);

  const handleSendInquiry = useCallback(() => {
    dispatch(resetInquiry());
    setInquiryOpen(true);
  }, [dispatch]);

  const handleInquirySuccess = useCallback(() => {
    setInquiryOpen(false);
    navigate('/thank-you');
  }, [navigate]);

  const handleStartOver = useCallback(() => {
    dispatch(discardDraft());
    dispatch(clearSelections());
    dispatch(setAreaType('county'));
    dispatch(setAreaRefId(null));
  }, [dispatch]);

  // Gate: the intake form is only reachable by a verified visitor. While the
  // session check is still pending we render nothing; once it resolves, an
  // unverified visitor is sent back to the landing page to verify.
  if (!verified) {
    return sessionChecked ? <Navigate to="/" replace /> : null;
  }

  const bodySx = isMobile
    ? {
      display: 'flex', flexDirection: 'column', overflow: 'visible', pb: '120px',
    }
    : {
      display: 'flex', flex: 1, minHeight: 0,
    };

  const areaColSx = {
    p: 2,
    bgcolor: 'background.paper',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    ...(isMobile
      ? { width: '100%', borderBottom: 1, borderColor: 'divider' }
      : {
        width: layout.areaColumnWidth, flexShrink: 0, overflowY: 'auto', borderRight: 1, borderColor: 'divider',
      }),
  };

  const mainSx = {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    p: 2,
    gap: 1.5,
    overflow: isMobile ? 'visible' : 'hidden',
  };

  const productsGridSx = isMobile
    ? {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: 2,
      overflow: 'visible',
    }
    : {
      // Single full-height row of columns. Each column is bounded to the row
      // height (gridTemplateRows: minmax(0,1fr)) so it scrolls vertically inside
      // its own container instead of overflowing the page. When the columns no
      // longer fit side by side, horizontal scrolling is confined to this grid
      // rather than spilling onto the whole screen.
      display: 'grid',
      gridAutoFlow: 'column',
      gridAutoColumns: 'minmax(240px, 1fr)',
      gridTemplateRows: 'minmax(0, 1fr)',
      gap: 2,
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      overflowX: 'auto',
      overflowY: 'hidden',
      // Keep sideways swipes inside the grid (no page rubber-band / back-nav).
      overscrollBehaviorX: 'contain',
      pb: 1,
      // Fade content toward the edges that have more categories off-screen.
      maskImage: categoryFadeMask(canLeft, canRight),
      WebkitMaskImage: categoryFadeMask(canLeft, canRight),
    };

  // Desktop wraps the grid so the overflow chips can pin to its edges without
  // scrolling away with the content.
  const gridWrapSx = {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
  };

  // Subtle circular chevron pinned to a grid edge. The edge fade does the
  // primary "more content" signalling; this is the small explicit control for
  // mouse/keyboard users. Fades in only when that direction can scroll.
  const categoryNavBtnSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    width: 28,
    height: 28,
    bgcolor: 'background.paper',
    color: 'text.secondary',
    border: 1,
    borderColor: 'divider',
    boxShadow: 1,
    transition: 'opacity 0.2s',
    '&:hover': { bgcolor: 'background.paper', color: 'text.primary' },
  };

  const areaColumn = (
    <Box sx={areaColSx}>
      <AreaTypePicker />
      {areaType === 'county' && <CountyDropdown />}
      {areaType === 'city' && <CityDropdown />}
      {areaType === 'custom' && <CustomGeometryUploader />}
      <StatsPanel
        areaSqMi={stats.data?.areaSqMi}
        tileAreaSqMi={stats.data?.tileAreaSqMi}
        estPopulation={stats.data?.estPopulation}
        tileEstPopulation={stats.data?.tileEstPopulation}
        loading={stats.loading}
        error={stats.error}
        sourceLabel={settings.population_source_label}
      />
      <LocationHighlightSvg
        areaType={areaType}
        countyFips={stats.data?.countyFips}
        displayGeoJson={stats.data?.displayGeoJson}
        tileGeoJson={stats.data?.tileGeoJson}
        name={stats.data?.name}
        loading={stats.loading}
        regionName={settings.region_display_name}
        fill={!isMobile}
        participation={participation}
        showParticipation
      />
    </Box>
  );

  const productGrid = (
    <Box ref={gridRef} sx={productsGridSx}>
      {(catalog.data?.categories ?? []).map((category) => (
        <ProductColumn
          key={category.id}
          category={category}
          products={catalog.data.products}
          options={catalog.data.options}
          isMobile={isMobile}
        />
      ))}
    </Box>
  );

  const desktopCategoryRow = (
    <Box sx={gridWrapSx}>
      {productGrid}
      <IconButton
        aria-label="Show previous categories"
        aria-hidden={!canLeft}
        tabIndex={canLeft ? 0 : -1}
        onClick={() => scrollCategories(-1)}
        sx={{
          ...categoryNavBtnSx, left: 2, opacity: canLeft ? 0.9 : 0, pointerEvents: canLeft ? 'auto' : 'none',
        }}
      >
        <KeyboardArrowLeftIcon fontSize="small" />
      </IconButton>
      <IconButton
        aria-label="Show more categories"
        aria-hidden={!canRight}
        tabIndex={canRight ? 0 : -1}
        onClick={() => scrollCategories(1)}
        sx={{
          ...categoryNavBtnSx, right: 2, opacity: canRight ? 0.9 : 0, pointerEvents: canRight ? 'auto' : 'none',
        }}
      >
        <KeyboardArrowRightIcon fontSize="small" />
      </IconButton>
      {/* Screen-reader note: the category row scrolls horizontally. */}
      <Box
        component="span"
        sx={{
          position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden',
        }}
      >
        {`Product categories scroll horizontally. ${categoryCount} categories total.`}
      </Box>
    </Box>
  );

  let productColumns;
  if (!catalog.data) {
    productColumns = (
      <Typography variant="body2" color={catalog.error ? 'error' : 'text.secondary'} sx={{ py: 1 }}>
        {catalog.loading && 'Loading products…'}
        {catalog.error}
      </Typography>
    );
  } else if (isMobile) {
    productColumns = productGrid;
  } else {
    productColumns = desktopCategoryRow;
  }

  return (
    <>
      <Box sx={{
        height: isMobile ? 'auto' : '100vh',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
      >
        <Box
          component="header"
          sx={{
            px: 3, pt: 2.5, pb: 1.5, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', flexShrink: 0,
          }}
        >
          {isMobile ? (
            // Mobile: vendor + toggle on the top line, partner logos wrap below,
            // then the title/subtitle get the full content width.
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
              }}
              >
                <Box sx={{ color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                  <SanbornLogo height={56} href="https://sanborn.com/" />
                </Box>
                <ThemeModeToggle />
              </Box>
              <LogoBar logos={logos} height={34} maxWidth={140} />
              <Box>
                <Typography variant="h6" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {settings.intake_header_title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {settings.intake_header_subtitle}
                </Typography>
              </Box>
            </Box>
          ) : (
            // Desktop: vendor + title on the left, partner logos pushed right,
            // theme toggle pinned to the far right.
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{
                color: 'text.primary', display: 'flex', alignItems: 'center', flexShrink: 0,
              }}
              >
                <SanbornLogo height={80} href="https://sanborn.com/" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {settings.intake_header_title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {settings.intake_header_subtitle}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              <LogoBar
                logos={logos}
                height={48}
                maxWidth={170}
                sx={{ flexShrink: 1, minWidth: 0 }}
              />
              <ThemeModeToggle />
            </Box>
          )}
        </Box>

        <Box sx={bodySx}>
          {areaColumn}
          <Box component="main" aria-label="Product selection" sx={mainSx}>
            {draft && draft.hasContent && (
              <ResumeDraftBanner onStartOver={handleStartOver} />
            )}
            {productColumns}
            <IncludedProductsAnnouncer />
          </Box>
          {!isMobile && (
            <Box sx={{
              width: layout.summaryColumnWidth, flexShrink: 0, overflowY: 'auto', p: 2,
            }}
            >
              <EstimateSummaryPanel onSendInquiry={handleSendInquiry} />
            </Box>
          )}
        </Box>
      </Box>

      {isMobile && (
        <EstimateSummaryDrawer onSendInquiry={handleSendInquiry} />
      )}

      <SendInquiryModal
        open={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        onSuccess={handleInquirySuccess}
      />
    </>
  );
};

export default IntakeFormPage;
